import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getUploadComponent,
    requestUploadUrl,
    uploadFileToS3,
    deleteFile as deleteFileApi,
    computeSha256,
    BarnApiError,
    UploadComponentConfig,
} from '../../services/barnApi';

export type FileUploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export type ManagedFile = {
    id: string;
    file: File;
    fileId?: string;
    status: FileUploadStatus;
    progress: number;
    errorMessage?: string;
    retriable?: boolean;
};

export type FileManagerMessages = {
    maxFilesReached: string;
    fileTypeNotAllowed: string;
    fileSizeExceeded: string;
    uploadFailed: string;
    configLoadFailed: string;
};

const DEFAULT_MESSAGES: FileManagerMessages = {
    maxFilesReached: 'Quantidade máxima de arquivos atingida ({max})',
    fileTypeNotAllowed: 'Tipo de arquivo "{type}" não é permitido. Aceitos: {accepted}',
    fileSizeExceeded: 'Tamanho do arquivo ({size}MB) excede o máximo de {max}MB',
    uploadFailed: 'Falha ao enviar arquivo',
    configLoadFailed: 'Falha ao carregar configuração de upload',
};

type UseFileManagerOptions = {
    spaceName: string;
    metadata?: Record<string, unknown>;
    // Upload TIPADO (opcional, retrocompatível — AF-4107). Repassados ao requestUploadUrl.
    // Sem `documentType`, o upload segue sem tipo (comportamento atual das demais telas).
    // `referenceDate`/`documentDate` (YYYY-MM-DD) só devem ir COM `documentType` (backend → 422).
    documentType?: string;
    referenceDate?: string;
    documentDate?: string;
    // Modo de disparo do upload (opcional, retrocompatível — AF-4107). Default true = comportamento
    // atual (addFiles já enfileira o upload imediatamente). Quando false, addFiles apenas ENCADEIA
    // os arquivos na lista (status 'idle') SEM enfileirar; o upload só dispara em startUploads().
    // Permite o fluxo "staged" do document-management (metadados definidos em modal antes do envio).
    autoStart?: boolean;
    messages?: Partial<FileManagerMessages>;
    // Quando true, o arquivo sai da lista do FileManager assim que o upload conclui (após o
    // onUploadComplete) — SEM chamar delete no barn. Para telas em que o arquivo é "consolidado"
    // em outro lugar no onUploadComplete (o recovery cria o vínculo do anexo na hora): manter o
    // arquivo na fila deixaria o mesmo objeto em dois lugares, e a lixeira do FileManager
    // apagaria do barn um objeto que já virou anexo do caso (→ 404 ao baixar). Default false:
    // as telas onde o fileId fica preso ao formulário (contract, boleto) seguem iguais.
    clearOnComplete?: boolean;
    onUploadComplete?: (fileId: string, file: File) => void;
    onUploadError?: (file: File, error: string) => void;
};

type UseFileManagerReturn = {
    config: UploadComponentConfig | null;
    files: ManagedFile[];
    fileIds: string[];
    isLoading: boolean;
    error: string | null;
    addFiles: (files: File[]) => void;
    removeFile: (id: string) => void;
    removeAll: () => void;
    removeSelected: (ids: string[]) => void;
    retry: (id: string) => void;
    // Dispara o upload de todos os arquivos encadeados ('idle'). Usado no modo staged
    // (autoStart=false); no modo imediato (default) os arquivos já são enviados em addFiles.
    startUploads: () => void;
};

let fileCounter = 0;
const generateId = () => `file-${++fileCounter}-${Date.now()}`;

const MAX_CONCURRENT_UPLOADS = 3;

export const useFileManager = ({
    spaceName,
    metadata,
    documentType,
    referenceDate,
    documentDate,
    autoStart = true,
    messages: customMessages,
    clearOnComplete = false,
    onUploadComplete,
    onUploadError,
}: UseFileManagerOptions): UseFileManagerReturn => {
    const msgs = { ...DEFAULT_MESSAGES, ...customMessages };
    const [config, setConfig] = useState<UploadComponentConfig | null>(null);
    const [files, setFiles] = useState<ManagedFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const uploadQueueRef = useRef<ManagedFile[]>([]);
    const activeUploadsRef = useRef(0);
    // Espelha a lista atual para o startUploads ler os arquivos encadeados ('idle') sem precisar
    // declarar `files` como dependência (evita novos avisos react-hooks/exhaustive-deps).
    const filesRef = useRef<ManagedFile[]>([]);
    filesRef.current = files;

    // Load space config on mount
    useEffect(() => {
        let cancelled = false;

        const loadConfig = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const result = await getUploadComponent(spaceName);
                if (!cancelled) setConfig(result);
            } catch (err) {
                if (!cancelled) {
                    console.warn('Failed to load upload config:', err);
                    setError(msgs.configLoadFailed);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        loadConfig();
        return () => { cancelled = true; };
    }, [spaceName]);

    const uploadFile = useCallback(async (managedFile: ManagedFile, attempt: number = 0): Promise<void> => {
        const { file, id } = managedFile;
        const MAX_RETRIES = 2;

        if (attempt === 0) {
            setFiles(prev => prev.map(f =>
                f.id === id ? { ...f, status: 'uploading' as const, progress: 0, errorMessage: undefined } : f
            ));
        }

        try {
            // Compute SHA-256 if deduplication is enabled
            let hashSha256: string | undefined;
            if (config?.deduplicate) {
                hashSha256 = await computeSha256(file);
            }

            // Request presigned URL from barn. Campos tipados (AF-4107) só entram quando há
            // documentType; datas só acompanham o tipo (o backend recusa datas sem tipo → 422).
            const { fileId, uploadUrl, reused } = await requestUploadUrl({
                spaceName,
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
                fileSize: file.size,
                hashSha256,
                metadata,
                ...(documentType
                    ? {
                        documentType,
                        ...(referenceDate ? { referenceDate } : {}),
                        ...(documentDate ? { documentDate } : {}),
                    }
                    : {}),
            });

            // Upload directly to S3 (skip if file was reused)
            if (!reused) {
                await uploadFileToS3(uploadUrl, file, (percent) => {
                    setFiles(prev => prev.map(f =>
                        f.id === id ? { ...f, progress: percent } : f
                    ));
                });
            }

            onUploadComplete?.(fileId, file);

            // clearOnComplete: o arquivo já foi consolidado pelo consumidor (ex.: virou anexo do
            // caso no recovery), então sai da lista do FileManager — sem delete no barn, que
            // apagaria o objeto agora referenciado pelo anexo. Sem a flag, marca success e
            // mantém na lista (comportamento das demais telas).
            if (clearOnComplete) {
                setFiles(prev => prev.filter(f => f.id !== id));
            } else {
                setFiles(prev => prev.map(f =>
                    f.id === id ? { ...f, status: 'success' as const, progress: 100, fileId } : f
                ));
            }
        } catch (err) {
            const retriable = !(err instanceof BarnApiError && err.status === 422);

            // Auto-retry on network errors
            if (retriable && attempt < MAX_RETRIES) {
                const delay = (attempt + 1) * 1000; // 1s, 2s
                await new Promise(resolve => setTimeout(resolve, delay));
                return uploadFile(managedFile, attempt + 1);
            }

            // Use backend message for API errors (422), configurable message for everything else
            const errorMessage = (err instanceof BarnApiError) ? err.message : msgs.uploadFailed;
            if (!(err instanceof BarnApiError)) {
                console.warn('Upload error:', err);
            }
            setFiles(prev => prev.map(f =>
                f.id === id ? { ...f, status: 'error' as const, errorMessage, retriable } : f
            ));
            onUploadError?.(file, errorMessage);
        }
    }, [spaceName, metadata, documentType, referenceDate, documentDate, config, onUploadComplete, onUploadError]);

    const processQueue = useCallback(() => {
        while (activeUploadsRef.current < MAX_CONCURRENT_UPLOADS && uploadQueueRef.current.length > 0) {
            const next = uploadQueueRef.current.shift()!;
            activeUploadsRef.current++;
            uploadFile(next).finally(() => {
                activeUploadsRef.current--;
                processQueue();
            });
        }
    }, [uploadFile]);

    const enqueueUploads = useCallback((managedFiles: ManagedFile[]) => {
        const valid = managedFiles.filter(f => f.status === 'idle');
        uploadQueueRef.current.push(...valid);
        processQueue();
    }, [processQueue]);

    const validateFile = useCallback((file: File): string | null => {
        if (!config) return msgs.configLoadFailed;

        // Validate content type
        if (!config.allowedTypes.includes('*')) {
            if (!config.allowedTypes.includes(file.type)) {
                return msgs.fileTypeNotAllowed
                    .replace('{type}', file.type || 'desconhecido')
                    .replace('{accepted}', config.allowedTypes.join(', '));
            }
        }

        // Validate size
        const maxBytes = config.maxFileSizeMb * 1024 * 1024;
        if (file.size > maxBytes) {
            return msgs.fileSizeExceeded
                .replace('{size}', (file.size / 1024 / 1024).toFixed(1))
                .replace('{max}', String(config.maxFileSizeMb));
        }

        return null;
    }, [config, msgs]);

    const addFiles = useCallback((newFiles: File[]) => {
        if (!config) return;

        // Validate max files count
        const currentCount = files.length;
        const maxAllowed = config.multipleFiles ? config.maxFiles : 1;
        const available = maxAllowed - currentCount;

        if (available <= 0) {
            setError(msgs.maxFilesReached.replace('{max}', String(maxAllowed)));
            return;
        }

        const filesToAdd = newFiles.slice(0, available);

        const managedFiles: ManagedFile[] = filesToAdd.map(file => {
            const validationError = validateFile(file);
            return {
                id: generateId(),
                file,
                status: validationError ? 'error' as const : 'idle' as const,
                progress: 0,
                errorMessage: validationError || undefined,
            };
        });

        setFiles(prev => [...prev, ...managedFiles]);
        setError(null);

        // Modo imediato (default): enfileira os válidos para upload (respeita a concorrência).
        // Modo staged (autoStart=false): apenas encadeia; o upload dispara em startUploads().
        if (autoStart !== false) {
            enqueueUploads(managedFiles);
        }
    }, [config, files.length, validateFile, enqueueUploads, autoStart]);

    const removeFile = useCallback(async (id: string) => {
        const managed = files.find(f => f.id === id);

        // If file was uploaded successfully, delete from backend
        if (managed?.fileId) {
            try {
                await deleteFileApi(managed.fileId);
            } catch (err) {
                console.warn('Failed to delete file from backend:', err);
            }
        }

        setFiles(prev => prev.filter(f => f.id !== id));
    }, [files]);

    const removeAll = useCallback(async () => {
        // Clear pending queue to prevent orphan uploads
        uploadQueueRef.current = [];

        const uploadedFiles = files.filter(f => f.fileId);
        await Promise.allSettled(
            uploadedFiles.map(f => deleteFileApi(f.fileId!).catch(() => { }))
        );

        setFiles([]);
    }, [files]);

    const removeSelected = useCallback(async (ids: string[]) => {
        // Remove from queue if still pending
        uploadQueueRef.current = uploadQueueRef.current.filter(f => !ids.includes(f.id));

        const toDelete = files.filter(f => ids.includes(f.id) && f.fileId);
        await Promise.allSettled(
            toDelete.map(f => deleteFileApi(f.fileId!).catch(() => { }))
        );

        setFiles(prev => prev.filter(f => !ids.includes(f.id)));
    }, [files]);

    const startUploads = useCallback(() => {
        // Reaproveita o caminho enqueueUploads/processQueue (sem duplicar lógica de upload).
        // enqueueUploads já filtra por status 'idle'.
        enqueueUploads(filesRef.current);
    }, [enqueueUploads]);

    const retry = useCallback((id: string) => {
        const managed = files.find(f => f.id === id);
        if (managed && managed.status === 'error') {
            enqueueUploads([{ ...managed, status: 'idle' }]);
        }
    }, [files, enqueueUploads]);

    const fileIds = files
        .filter(f => f.status === 'success' && f.fileId)
        .map(f => f.fileId!);

    return {
        config,
        files,
        fileIds,
        isLoading,
        error,
        addFiles,
        removeFile,
        removeAll,
        removeSelected,
        retry,
        startUploads,
    };
};
